/**
 * Benefits Capsule Scroll - JS-Controlled Sticky
 * Uses position:fixed with JS instead of CSS sticky
 */

(function() {
  'use strict';

  const SELECTORS = {
    section: '[data-benefits-capsule-scroll]',
    stickyWrapper: '.bcs-sticky-wrapper',
    placeholder: '.bcs-placeholder',
    capsule: '[data-capsule]',
    benefit: '[data-benefit-index]',
    benefitDesc: '[data-benefit-desc-index]',
    mobileBenefits: '[data-mobile-benefits]',
    mobileBenefit: '[data-mobile-benefit]',
    dot: '[data-dot]',
    scrollSpacer: '.bcs-scroll-spacer'
  };

  class BenefitsCapsuleScroll {
    constructor(section) {
      this.section = section;
      this.count = parseInt(section.dataset.benefitCount) || 0;
      this.currentIndex = 0;
      this.isFixed = false;
      this.isEnd = false;
      this.isHidden = false;
      this.isResizing = false;
      this.isDesktop = window.innerWidth > 990;
      
      if (this.count === 0) {
        console.warn('BenefitsCapsuleScroll: No benefits found');
        return;
      }
      
      this.cacheElements();
      this.createPlaceholder();
      this.bindEvents();
      
      // Fix spacer height - on ALL devices (desktop + mobile)
      if (this.scrollSpacer) {
        const isMobile = window.innerWidth <= 990;
        // Mobile: 1.6x for slower transition so users can read benefits
        const multiplier = isMobile ? 1.6 : 1;
        const fixedHeight = this.count * window.innerHeight * multiplier;
        this.scrollSpacer.style.cssText = `display: block !important; height: ${fixedHeight}px !important; min-height: ${fixedHeight}px !important;`;
        // Force reflow
        this.scrollSpacer.offsetHeight;
        console.log('BenefitsCapsuleScroll: Set spacer height to', fixedHeight, isMobile ? '(mobile)' : '(desktop)');
      }
      
      this.init();
      
      console.log('BenefitsCapsuleScroll: Initialized with', this.count, 'benefits');
      console.log('BenefitsCapsuleScroll: Final spacer height =', this.scrollSpacer?.offsetHeight);
    }

    cacheElements() {
      this.stickyWrapper = this.section.querySelector(SELECTORS.stickyWrapper);
      this.capsule = this.section.querySelector(SELECTORS.capsule);
      this.benefits = this.section.querySelectorAll(SELECTORS.benefit);
      this.descriptions = this.section.querySelectorAll(SELECTORS.benefitDesc);
      this.mobileBenefits = this.section.querySelector(SELECTORS.mobileBenefits);
      this.mobileBenefitItems = this.section.querySelectorAll(SELECTORS.mobileBenefit);
      this.dots = this.section.querySelectorAll(SELECTORS.dot);
      this.scrollSpacer = this.section.querySelector(SELECTORS.scrollSpacer);
    }

    createPlaceholder() {
      this.placeholder = document.createElement('div');
      this.placeholder.className = 'bcs-placeholder';
      this.stickyWrapper.parentNode.insertBefore(this.placeholder, this.stickyWrapper);
    }

    bindEvents() {
      let resizeTimer;
      let lastWidth = window.innerWidth;

      window.addEventListener('resize', () => {
        const newWidth = window.innerWidth;

        // Mobile browser chrome (URL bar) hiding/showing fires resize with height-only change.
        // Ignore those — recalculating spacer height on every URL bar toggle causes scroll jumps.
        if (newWidth === lastWidth) {
          return;
        }

        clearTimeout(resizeTimer);

        // Pause scroll handling during resize to prevent flicker
        this.isResizing = true;

        resizeTimer = setTimeout(() => {
          const newWidth = window.innerWidth;
          const wasDesktop = lastWidth > 990;
          const nowDesktop = newWidth > 990;
          const modeChanged = wasDesktop !== nowDesktop;
          
          // Update isDesktop flag
          this.isDesktop = nowDesktop;
          lastWidth = newWidth;
          
          // Recalculate spacer height on resize
          if (this.scrollSpacer) {
            const isMobile = newWidth <= 990;
            const multiplier = isMobile ? 1.6 : 1;
            const fixedHeight = this.count * window.innerHeight * multiplier;
            this.scrollSpacer.style.cssText = `display: block !important; height: ${fixedHeight}px !important; min-height: ${fixedHeight}px !important;`;
            // Force reflow before recalculating positions
            this.scrollSpacer.offsetHeight;
            
            // Reset wrapper state completely
            this.stickyWrapper.classList.remove('is-fixed', 'is-end');
            this.stickyWrapper.style.transform = '';
            this.stickyWrapper.style.opacity = '';
            this.isFixed = false;
            this.isEnd = false;
            this.placeholder.classList.remove('is-active');
            this.placeholder.style.height = '0';
            
            // Recalculate enter/exit points AFTER layout settles
            requestAnimationFrame(() => {
              const getAbsoluteTop = (el) => {
                let top = 0;
                while (el) { top += el.offsetTop; el = el.offsetParent; }
                return top;
              };
              this.cachedSpacerHeight = fixedHeight;
              this.cachedEnterPoint = getAbsoluteTop(this.section);
              this.cachedExitPoint = getAbsoluteTop(this.scrollSpacer) + fixedHeight - window.innerHeight;
              
              console.log('[BCS] Resize complete. Mode:', nowDesktop ? 'desktop' : 'mobile', 'Enter:', this.cachedEnterPoint, 'Exit:', this.cachedExitPoint);
              
              // Resume scroll handling and trigger immediate update
              this.isResizing = false;
              if (this.scrollHandler) {
                this.scrollHandler();
              }
            });
          } else {
            this.isResizing = false;
          }
        }, 100);
      });

      // Handle orientation change separately (important for mobile)
      window.addEventListener('orientationchange', () => {
        this.isResizing = true;
        // Wait for orientation change to complete
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      });

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.dot);
          this.scrollToBenefit(index);
        });
      });

      if (this.mobileBenefits) {
        this.mobileBenefits.addEventListener('scroll', this.handleMobileScroll.bind(this));
      }
    }

    init() {
      this.goToBenefit(0);
      
      if (this.capsule) {
        this.capsule.classList.remove('is-open');
        this.capsule.style.setProperty('--capsule-open', 0);
      }
      
      // Always use sticky scroll animation (desktop + mobile)
      this.initDesktopScroll();
    }

    initDesktopScroll() {
      // Get absolute position of spacer (more reliable than section)
      const getAbsoluteTop = (el) => {
        let top = 0;
        while (el) { top += el.offsetTop; el = el.offsetParent; }
        return top;
      };
      
      // Cache positions based on spacer location
      const spacerTop = getAbsoluteTop(this.scrollSpacer);
      const spacerHeight = this.scrollSpacer ? this.scrollSpacer.offsetHeight : window.innerHeight * this.count;
      
      // Enter when reaching section, exit BEFORE spacer ends (so wrapper hides before next section appears)
      this.cachedEnterPoint = getAbsoluteTop(this.section);
      // Exit earlier: subtract one viewport height so capsule hides before next section is visible
      this.cachedExitPoint = spacerTop + spacerHeight - window.innerHeight;
      this.cachedSpacerHeight = spacerHeight;
      
      console.log('[BCS] Enter:', this.cachedEnterPoint, 'Exit:', this.cachedExitPoint);
      
      this.scrollHandler = () => {
        // Skip scroll handling during resize to prevent flicker
        if (this.isResizing) return;
        
        const scrollY = window.scrollY;
        const wrapperHeight = this.stickyWrapper.offsetHeight;
        
        const enterPoint = this.cachedEnterPoint;
        const exitPoint = this.cachedExitPoint;
        const spacerHeight = this.cachedSpacerHeight;
        
        const scrollRange = exitPoint - enterPoint;
        const scrollInSection = scrollY - enterPoint;
        const progress = Math.max(0, Math.min(1, scrollInSection / scrollRange));
        
        const benefitIndex = Math.min(Math.floor(progress * this.count), this.count - 1);
        
        const shouldBeFixed = scrollY >= enterPoint && scrollY < exitPoint;
        const isPastEnd = scrollY >= exitPoint;
        // Hide completely once scrolled more than 1 viewport past the exit point
        const isFarPastEnd = scrollY >= exitPoint + window.innerHeight;

        if (shouldBeFixed) {
          if (!this.isFixed || this.isEnd || this.isHidden) {
            // Entering or re-entering fixed state (scrolling back up from end)
            this.isFixed = true;
            this.isEnd = false;
            this.isHidden = false;
            this.stickyWrapper.classList.add('is-fixed');
            this.stickyWrapper.classList.remove('is-end', 'is-hidden');
            this.stickyWrapper.style.transform = '';
            this.stickyWrapper.style.pointerEvents = '';
            this.placeholder.classList.add('is-active');
            this.placeholder.style.height = wrapperHeight + 'px';
          }
        } else if (isFarPastEnd) {
          // Far past end: completely hide the wrapper so it doesn't show behind footer
          if (!this.isHidden) {
            this.isFixed = false;
            this.isEnd = true;
            this.isHidden = true;
            this.stickyWrapper.classList.remove('is-fixed');
            this.stickyWrapper.classList.add('is-end', 'is-hidden');
            this.placeholder.classList.add('is-active');
            this.placeholder.style.height = wrapperHeight + 'px';
          }
        } else if (isPastEnd) {
          // Just past end: keep fixed but with low z-index for smooth transition
          if (!this.isEnd || this.isHidden) {
            this.isFixed = true;
            this.isEnd = true;
            this.isHidden = false;
            this.stickyWrapper.classList.add('is-fixed', 'is-end');
            this.stickyWrapper.classList.remove('is-hidden');
            this.stickyWrapper.style.transform = '';
            
            this.placeholder.classList.add('is-active');
            this.placeholder.style.height = wrapperHeight + 'px';
            
            // Ensure capsule is closed at the end
            if (this.capsule) {
              this.capsule.style.setProperty('--capsule-open', 0);
              this.capsule.classList.remove('is-open');
            }
          }
        } else {
          // Before section — reset everything
          if (this.isFixed || this.isEnd || this.isHidden) {
            this.isFixed = false;
            this.isEnd = false;
            this.isHidden = false;
            this.stickyWrapper.classList.remove('is-fixed', 'is-end', 'is-hidden');
            this.stickyWrapper.style.transform = '';
            this.placeholder.classList.remove('is-active');
            this.placeholder.style.height = '0';
          }
        }
        
        // Only update benefit display while in fixed state (not after exit)
        if (!isPastEnd && benefitIndex !== this.currentIndex && benefitIndex >= 0) {
          this.goToBenefit(benefitIndex);
        }
        
        // Only animate capsule while not past end
        if (!isPastEnd) {
          this.updateCapsuleAnimation(progress);
        }
      };
      
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      requestAnimationFrame(() => this.scrollHandler());
    }

    updateCapsuleAnimation(progress) {
      if (!this.capsule) return;
      
      // Animation: opens at start, stays open, closes at end
      let openAmount;
      
      if (progress <= 0.10) {
        // Opening phase: 0% to 10% scroll
        openAmount = progress / 0.10;
      } else if (progress >= 0.85) {
        // Closing phase: 85% to 100% scroll
        openAmount = (1 - progress) / 0.15;
      } else {
        // Fully open during benefits
        openAmount = 1;
      }
      
      // Smooth easing
      openAmount = this.easeInOutCubic(Math.max(0, Math.min(1, openAmount)));
      
      this.capsule.style.setProperty('--capsule-open', openAmount);
      
      // Hide header immediately when scrolling starts (cleaner mobile UX)
      const header = this.section.querySelector('.bcs-header');
      if (header) {
        if (progress > 0.01) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }
      }
      
      // Clear any residual opacity (don't touch transform — scroll handler controls it)
      if (this.stickyWrapper) {
        this.stickyWrapper.style.opacity = '';
      }
      
      if (openAmount > 0.15) {
        this.capsule.classList.add('is-open');
      } else {
        this.capsule.classList.remove('is-open');
      }
    }
    
    easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    initMobile() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      
      this.reset();
      
      if (this.capsule) {
        this.capsule.classList.add('is-open');
        this.capsule.style.setProperty('--capsule-open', 1);
      }
    }

    handleMobileScroll() {
      if (!this.mobileBenefits || !this.mobileBenefitItems.length) return;
      
      const scrollLeft = this.mobileBenefits.scrollLeft;
      const itemWidth = this.mobileBenefitItems[0].offsetWidth;
      const gap = 20;
      const newIndex = Math.round(scrollLeft / (itemWidth + gap));
      
      if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.count) {
        this.currentIndex = newIndex;
        this.updateDots(newIndex);
      }
    }

    goToBenefit(index) {
      if (index < 0 || index >= this.count) return;
      
      this.currentIndex = index;
      
      this.benefits.forEach(benefit => {
        const i = parseInt(benefit.dataset.benefitIndex);
        benefit.classList.toggle('is-active', i === index);
      });
      
      this.descriptions.forEach(desc => {
        const i = parseInt(desc.dataset.benefitDescIndex);
        desc.classList.toggle('is-active', i === index);
      });
      
      this.updateDots(index);
    }

    scrollToBenefit(index) {
      if (index < 0 || index >= this.count) return;
      
      const sectionRect = this.section.getBoundingClientRect();
      const sectionTop = window.scrollY + sectionRect.top;
      const spacerHeight = this.scrollSpacer ? this.scrollSpacer.offsetHeight : window.innerHeight * this.count;
      
      const targetProgress = (index + 0.5) / this.count;
      const targetScroll = sectionTop + (targetProgress * spacerHeight);
      
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }

    updateDots(index) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    reset() {
      // No DOM movement needed - just reset classes
      this.isFixed = false;
      this.isEnd = false;
      this.stickyWrapper.classList.remove('is-fixed', 'is-end');
      this.stickyWrapper.style.transform = '';
      this.placeholder.classList.remove('is-active');
      this.placeholder.style.height = '0';
    }

    destroy() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      this.reset();
    }
  }

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach(section => {
      new BenefitsCapsuleScroll(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SELECTORS.section);
    if (section) new BenefitsCapsuleScroll(section);
  });

})();
